import { useAppDialog } from '../appDialog'
import { useContextMenu } from '@/components/contextMenu'
import { useNavigatorStore } from '@/store/navigator'
import { useMessage, TreeDropInfo } from 'naive-ui'
import type { TreeOption } from 'naive-ui'
import { defineAsyncComponent, ref, computed, h, watch, reactive } from 'vue'
import type { DefineComponent } from 'vue'
import { useAcl } from 'vue-simple-acl'
import aclRules from '@teams/common/acl/aclRules'
import Icon from '@teams/common/components/icon'
import { useI18n } from 'vue-i18n'
import ConfirmRemoveNav from './ConfirmRemoveNav.vue'
import { useRouter } from 'vue-router'
import defaultAppIcon from '../../assets/defaultAppIcon.svg'
import { NavKind } from '@teams/common/src/api/nav/types'
import { useGroupStore } from '../../store/group'
export interface NavApp {
  appId: number
  appName: string
  appIcon: string
  appLink: string
  appKind: NavKind
  position: number
}
interface RenderParams {
  option: TreeOption
  checked: boolean
  selected: boolean
}
type DraggaleChangeEvents = 'added' | 'removed' | 'moved'

interface DraggaleChangeEventParams<T> {
  element: T
  newIndex?: number
  oldIndex?: number
}
type DraggaleChangeParams<T> = Record<
  DraggaleChangeEvents,
  DraggaleChangeEventParams<T>
>
function useNavApplication() {
  const CreateNewAppDialog = defineAsyncComponent(
    () => import('./CreateNewAppDialog.vue')
  )

  const SetPermissionDialog = defineAsyncComponent(
    () => import('./SetPermissionDialog.vue')
  )
  const acl = useAcl()
  const showAppAdd = computed(() => {
    return acl.can(aclRules.navigation_add_tag)
  })
  // 无app且无app添加权限时，不展示app模块
  const showAppModule = computed(() => {
    if (appList.value.length === 0 && showAppAdd.value === false) {
      return false
    } else {
      return true
    }
  })
  const rbacDeleteTag = computed(() => acl.can(aclRules.navigation_delete_tag))
  const rbacUpdateTag = computed(() => acl.can(aclRules.navigation_update_tag))
  const appSortable = computed(() =>
    acl.can(aclRules.navigation_update_tag_order)
  )
  const router = useRouter()
  const dialog = useAppDialog()
  const contextMenu = useContextMenu()
  const navigatorStore = useNavigatorStore()
  const appList = computed(() => {
    return [...navigatorStore.appList].map(item => ({ ...item }))
  })
  const selectedApp = ref<NavApp>() // 右键菜单选中时的应用
  const message = useMessage()
  const { t } = useI18n()
  const addAppOptions = computed(() => {
    const options = [
      {
        label: '从应用市场添加',
        key: 'store',
        icon: () => h(Icon, { name: 'appStore', fill: 'transparent' }),
        filter: rbacUpdateTag.value,
        props: {}
      },
      {
        label: '从网页链接添加',
        key: 'link',
        icon: () => h(Icon, { name: 'appLink', fill: 'transparent' }),
        filter: rbacUpdateTag.value,
        props: {}
      }
    ].filter(({ filter }) => (filter === undefined ? true : filter))

    return options
  })
  const gotoCreatApp = (e: MouseEvent) => {
    contextMenu.show({
      menus: addAppOptions.value,
      x: e.clientX,
      y: e.clientY,
      onSelect: key => handleAddAppComand(key as string)
    })
  }
  const handleAddAppComand = (key: string) => {
    switch (key) {
      case 'store':
        creatAppInStore()
        break
      case 'link':
        creatAppByLink()
        break
      default:
        break
    }
    contextMenu.close()
  }
  const creatAppInStore = () => {
    const groupStore = useGroupStore()
    const groupId = groupStore.groupId
    const url = `https://co.wps.cn/workbench/?vcl=v2#/team/office_8880?spaceId=${groupId}`
    window.open(url)
  }
  const creatAppByLink = (appId?: number) => {
    dialog.show({
      name: 'createNewApp',
      title: appId ? '修改网页链接' : '添加网页链接',
      component: CreateNewAppDialog,
      showCloseIcon: true,
      componentProps: { appId: appId || 0 },
      componentEvents: {
        closeDialog: () => dialog.close()
      },
      footer: false,
      modalProps: {
        style: {
          width: '480px'
        }
      }
    })
  }
  const editAppInfo = (appId?: number) => {
    creatAppByLink(appId)
  }

  const openSetPermissionDialog = (appId: number) => {
    dialog.show({
      name: 'SetPermissionDialog',
      title: '该应用对谁可见',
      component: SetPermissionDialog,
      showCloseIcon: true,
      componentProps: { appId: appId },
      componentEvents: {
        closeDialog: () => dialog.close()
      },
      footer: false,
      modalProps: {
        style: {
          width: '320px'
        }
      },
      size: 'minWidth320'
    })
  }

  const appOptions = computed(() => {
    const options = [
      {
        label: '修改',
        key: 'edit',
        icon: () => h(Icon, { name: 'edit', fill: 'transparent' }),
        filter: rbacUpdateTag.value,
        props: {}
      },
      {
        label: '设置可见性',
        key: 'setPermission',
        icon: () => h(Icon, { name: 'setPermission', fill: 'transparent' }),
        filter: rbacUpdateTag.value,
        props: {}
      },
      {
        label: '移除',
        key: 'remove',
        icon: () => h(Icon, { name: 'redDelete', fill: 'transparent' }),
        filter: rbacDeleteTag.value,
        props: { class: 'special-color' }
      }
    ].filter(({ filter }) => (filter === undefined ? true : filter))

    return options
  })
  const handleAppBarComand = (key: string, id: number) => {
    switch (key) {
      case 'edit':
        editAppInfo(id)
        break
      case 'setPermission':
        openSetPermissionDialog(id)
        break
      case 'remove':
        confirmRemoveNav(id)
        break
      default:
        break
    }
    contextMenu.close()
  }

  const showAppOptions = (e: MouseEvent, nav: NavApp) => {
    selectedApp.value = nav
    contextMenu.show({
      menus: appOptions.value,
      x: e.clientX,
      y: e.clientY,
      onSelect: key => handleAppBarComand(key as string, nav.appId)
    })
  }
  const openAppLink = (link: string) => {
    const ignoreHint = localStorage.getItem('ignoreHint__k.wps.cn')
    if (
      ignoreHint === 'true' ||
      link.includes('wps.cn') ||
      link.includes('kdocs.cn')
    ) {
      let url
      if (link?.includes('http') || link?.includes('http')) {
        url = link
      } else {
        url = 'https://' + link
      }
      window.open(url)
    } else {
      const { href } = router.resolve({
        path: '/link',
        query: {
          link: link
        }
      })
      window.open(href, '_blank')
    }
  }
  const updateAppOrder = async (appId: number, nextAppId: number) => {
    try {
      await navigatorStore.updateAppPosition(appId, nextAppId)
    } catch (e) {
      await navigatorStore.getNavs()
      message.error((e as Error).message)
    }
  }
  const doRemoveApp = async (appId: number) => {
    try {
      await navigatorStore.removeAppLink(appId)
      message.success(t('base.removeSuccessfully'))
      dialog.close()
    } catch (e) {
      message.error((e as Error).message)
    }
  }
  const confirmRemoveNav = (appId: number) => {
    dialog.show({
      title: t('components.navigator.navs.confirmRemoveAppTitle'),
      modalProps: { style: { width: '480px' } },
      component: ConfirmRemoveNav,
      componentProps: {
        text: t('components.navigator.navs.removeAppTips'),
        img: selectedApp.value?.appIcon || defaultAppIcon,
        name: selectedApp.value?.appName
      },
      componentEvents: {
        confirm: () => doRemoveApp(appId)
      },
      footer: false
    })
  }
  // 水平导航栏的拖拽函数（适用于vuedraggle）
  const onAppListOrderChange = async (e: DraggaleChangeParams<any>) => {
    const { moved } = e
    if (!moved) {
      return
    }
    try {
      const { element, oldIndex = 0, newIndex = 0 } = moved
      let nextAppId: number
      // 往前或往后移动得区分拿值
      if (oldIndex < newIndex) {
        nextAppId =
          newIndex + 1 === appList.value.length
            ? -1
            : appList.value[newIndex + 1].appId
      } else {
        nextAppId =
          newIndex + 1 === appList.value.length
            ? -1
            : appList.value[newIndex].appId
      }
      await updateAppOrder(element.appId, nextAppId)
    } catch (e) {
      message.error((e as Error).message)
    }
  }
  // 垂直导航栏的拖拽函数（适用于tree）
  const changeApplistOrder = async ({
    node,
    dragNode,
    dropPosition
  }: TreeDropInfo) => {
    // node 指向的节点,dragNode 拖拽的节点,dropPosition 拖拽的节点与指向节点的关系（包括：after、start、inside)
    const temList = [...visibleAppList.value]
    const appId = dragNode.appId as number
    let nextAppId
    if (dropPosition === 'before') {
      nextAppId = node.appId
    }
    if (dropPosition === 'after') {
      const index = appList.value.findIndex(item => item.appId === node.appId)
      if (index === -1 || index + 1 === appList.value.length) {
        nextAppId = -1
      } else {
        nextAppId = appList.value[index + 1].appId
      }
    }
    // 界面数据及时响应
    const dragNodeIndex = temList.findIndex(
      item => item.appId === dragNode.appId
    )
    temList.splice(dragNodeIndex, 1)
    const nodeIndex = temList.findIndex(item => item.appId === node.appId)
    if (dropPosition === 'before') {
      temList.splice(nodeIndex, 0, dragNode)
    } else {
      temList.splice(nodeIndex + 1, 0, dragNode)
    }
    visibleAppList.value = [...temList]
    await handleDrop(appId, nextAppId)
  }
  // 垂直导航栏的拖拽函数（适用于tree）
  const handleDrop = async (appId, nextAppId) => {
    try {
      await updateAppOrder(appId, nextAppId)
    } catch (e) {
      message.error((e as Error).message)
    }
  }
  const isDraggling = ref(false)
  const dragstart = () => {
    isDraggling.value = true
  }
  const dragend = () => {
    isDraggling.value = false
  }
  const renderLabel = (
    params: RenderParams,
    type: 'vertical' | 'horizontal'
  ) => {
    const { option } = params
    const { appId, appName, appIcon, appLink, appKind, position } = option
    const AppItem = defineAsyncComponent(
      () => import('./vertical/appManagement/appItem.vue')
    )
    const appInfo: any = {
      appId: appId,
      appName: appName,
      appIcon: appIcon,
      appLink: appLink,
      appKind: appKind,
      position: position
    }
    return h(
      AppItem as DefineComponent,
      reactive({
        isDraggling,
        appInfo,
        type,
        openAppLink,
        showAppOptions
      })
    )
  }
  const isAllowDrop = (info: {
    dropPosition: 'before' | 'inside' | 'after'
    node: TreeOption
    phase: 'drag' | 'drop'
  }) => {
    const { dropPosition } = info
    if (dropPosition === 'inside') {
      return false
    } else {
      return true
    }
  }
  const visibleAppList = ref<TreeOption[]>([])
  watch(
    [() => appList.value],
    () => {
      visibleAppList.value = appList.value.map(item => ({
        appId: item.appId,
        appName: item.appName,
        appIcon: item.appIcon,
        appLink: item.appLink,
        appKind: item.appKind,
        position: item.position
      }))
    },
    {
      immediate: true
    }
  )
  const refreshNavs = async () => {
    if (document.visibilityState !== 'hidden') {
      await navigatorStore.getNavs()
    }
  }

  document.addEventListener('visibilitychange', refreshNavs)

  return {
    appList,
    visibleAppList,
    creatAppByLink,
    gotoCreatApp,
    showAppAdd,
    showAppModule,
    showAppOptions,
    openAppLink,
    onAppListOrderChange,
    changeApplistOrder,
    handleDrop,
    appSortable,
    isAllowDrop,
    isDraggling,
    dragstart,
    dragend,
    renderLabel
  }
}

export default useNavApplication
